import React, { useState, useEffect } from 'react';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import IconButton from '../../../components/Button/iconbutton';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';  // Icon for resetting
import CloseIcon from '@mui/icons-material/Close';
import TableComponent from '../../../components/Table/TableComponent';
import { TableCell, TableRow } from '@mui/material';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import axios from 'axios'; 
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import { apiHandler } from '../../../services/authService';

interface ProcedureOrderFormProps {
  patientId: string;
}

interface ProcedureOrder {
  id: number;
  date: string;
  code: string;
  description: string;
}

const ProcedureOrderForm: React.FC<ProcedureOrderFormProps> = ({ patientId }) => {
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [procedureOrders, setProcedureOrders] = useState<ProcedureOrder[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchProcedureOrders = async (params: any) => {
    setLoading(true);
    setError(null);
  
    try {
      const url        = `${BASE_URL}/resource/patientHealth`;
      const payload  = {
        limit: rowsPerPage,
      page: page + 1,
      searchkey: '',
      order_by: 'created_at',
      dir: 2,
      slug: 'procedure',
      user_id: patientId,
      from: dateRange.startDate || '',  // Pass the start date filter
      to: dateRange.endDate || '',    
      }
      const response   = await apiHandler('', 'GET', payload, url);

      if (!response) {
          throw new Error("No response received from the server");
      }
      
      // const response = await axios.get(`${BASE_URL}/resource/patientHealth`, {
      //   headers: { ...getAxiosConfig().headers }, // Ensure headers are properly passed
      //   params, // Pass params correctly
      // });
  
      const data = response.data.data.patient_healths;
      const total = response.data.data.pagination.total;
  
      setProcedureOrders(
        data.map((item: any) => ({
          id: item.id,
          code: item.values.code,
          date: item.values.date,
          description: item.values.name,
        }))
      );
      setTotalRecords(total);
    } catch (err) {
      setError('Failed to fetch procedure orders');
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    // Initial data fetch when the component mounts or when page, rowsPerPage, or patientId change
    const params = {
      limit: rowsPerPage,
      page: page + 1,
      searchkey: '',
      order_by: 'created_at',
      dir: 2,
      slug: 'procedure',
      user_id: patientId,
      from: dateRange.startDate || '',  // Pass the start date filter
      to: dateRange.endDate || '',      // Pass the end date filter
    };
    fetchProcedureOrders(params);
  }, [page, rowsPerPage, patientId, dateRange]);  // Add dateRange as a dependency

  const handleSearchClick = () => {
    // Create params based on the date range and fetch the filtered data
    const params = {
      limit: rowsPerPage,
      page: page + 1,
      searchkey: '',
      order_by: 'created_at',
      dir: 2,
      slug: 'procedure',
      user_id: patientId,
      from: dateRange.startDate,
      to: dateRange.endDate,
    };
    fetchProcedureOrders(params);
  };

  const handleResetDateRange = () => {
    // Reset the date range and fetch all data
    setDateRange({ startDate: '', endDate: '' });

    const params = {
      limit: rowsPerPage,
      page: page + 1,
      searchkey: '',
      order_by: 'created_at',
      dir: 2,
      slug: 'procedure',
      user_id: patientId,
      from: '',   // Reset date filter
      to: '',     // Reset date filter
    };
    fetchProcedureOrders(params);
  };

  const renderRow = (item: ProcedureOrder, index: number) => (
    <TableRow key={item.id}>
      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
      <TableCell>{item.date}</TableCell>
      <TableCell>{item.code}</TableCell>
      <TableCell>{item.description}</TableCell>
    </TableRow>
  );

  const handleDateRangeChange = (dates: { startDate: string; endDate: string }): void => {
    setDateRange(dates);
  };

  // Conditionally render the Reset button based on date range input
  const showResetButton = dateRange.startDate || dateRange.endDate;

  return (
    <div>
      <div className="flex items-center space-x-4 mb-6">
        <DateRangePicker
          startDateId="procedureOrderStartDate"
          endDateId="procedureOrderEndDate"
          onChange={handleDateRangeChange}
          value={dateRange}
          className="my-custom-class"
        />
        <IconButton icon={<SearchOutlinedIcon />} onClick={handleSearchClick} />
        {showResetButton && (
          <IconButton icon={<CloseIcon />} onClick={handleResetDateRange} />
        )} {/* Reset button only shows if a date range is selected */}
      </div>

      {loading && <div>Loading...</div>}
      {error && <div className="text-red-500">{error}</div>}

      <TableComponent
        headers={['No', 'Date', 'Code', 'Description']}
        data={procedureOrders}
        renderRow={renderRow}
      />

      <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage); 
          setPage(0); // Reset to first page
        }}
      />
    </div>
  );
};

export default ProcedureOrderForm;
